export type AttachmentOwnerType = 'daemonTrigger';

/**
 * 첨부가 연결되는 대상 엔티티 타입. 서버 일반화(daemonTrigger 전용 → 다대상)와 짝을 이룬다.
 * 조회 클라이언트/훅/모달이 이 타입으로 대상별 첨부를 가져온다.
 */
export type AttachmentTargetType = 'daemonTrigger' | 'codeReview' | 'completionReport' | 'document';

export type Attachment = {
  id: string;
  key: string;
  originalName: string;
  mimeType: string;
  size: number;
  uploadedByMemberId: string;
  createdAt: string;
};

/**
 * 작성 모달 메모리에서 사용. R2에는 이미 업로드 완료된 상태, trigger 생성 시 attachmentDrafts로 전달.
 * trigger commit 트랜잭션 내에서 Attachment row가 생성된다.
 */
export type PendingAttachmentDraft = {
  key: string;
  originalName: string;
  mimeType: string;
  size: number;
};

export const allowedAttachmentMimeTypes = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/markdown',
  'application/pdf',
  'text/html',
] as const;

export const officeAttachmentMimeTypes = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const;

export const documentAttachmentMimeTypes = [...allowedAttachmentMimeTypes, ...officeAttachmentMimeTypes] as const;

export const isOfficeAttachmentMimeType = (mimeType: string) =>
  (officeAttachmentMimeTypes as readonly string[]).includes(mimeType);

const officeAttachmentMimeTypeByExtension = {
  '.docx': officeAttachmentMimeTypes[0],
  '.pptx': officeAttachmentMimeTypes[1],
  '.xlsx': officeAttachmentMimeTypes[2],
} as const;

/**
 * 모든 첨부 대상에서 `text/plain`으로 받는 텍스트 확장자. 브라우저가 붙이는 MIME(`application/json`,
 * `.ts`의 `video/mp2t` 등)이 제각각이라 확장자로 정규화한다. OS가 더블클릭으로 바로 실행하는
 * 형식(js·vbs·ps1·bat·cmd·sh·command)은 넣지 않는다. CLI(`cli/src/commands/attachment.ts`)에 같은 값을 복제했다.
 */
export const textAttachmentExtensions = [
  '.sql',
  '.csv',
  '.tsv',
  '.json',
  '.jsonl',
  '.yaml',
  '.yml',
  '.toml',
  '.xml',
  '.ini',
  '.log',
  '.diff',
  '.patch',
  '.ts',
  '.tsx',
  '.jsx',
  '.py',
  '.go',
  '.rs',
  '.java',
  '.kt',
  '.swift',
  '.c',
  '.h',
  '.cpp',
  '.hpp',
  '.cs',
  '.rb',
  '.php',
  '.css',
  '.scss',
  '.graphql',
  '.proto',
  '.prisma',
] as const;

export const attachmentAccept = [...allowedAttachmentMimeTypes, ...textAttachmentExtensions].join(',');

export const documentAttachmentAccept = [
  attachmentAccept,
  ...officeAttachmentMimeTypes,
  ...Object.keys(officeAttachmentMimeTypeByExtension),
].join(',');

const fileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf('.');
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : '';
};

export const normalizeAttachmentMimeType = (
  file: { name: string; type: string },
  targetType?: AttachmentTargetType,
): string => {
  const extension = fileExtension(file.name);
  if ((textAttachmentExtensions as readonly string[]).includes(extension)) {
    return 'text/plain';
  }
  if (targetType !== 'document' || (file.type !== '' && file.type !== 'application/octet-stream')) {
    return file.type;
  }
  return (
    officeAttachmentMimeTypeByExtension[extension as keyof typeof officeAttachmentMimeTypeByExtension] ?? file.type
  );
};

const secretAttachmentFileNames = new Set([
  '.env',
  '.npmrc',
  '.pypirc',
  '.netrc',
  '.pgpass',
  '.git-credentials',
  '.htpasswd',
  'id_rsa',
  'id_dsa',
  'id_ecdsa',
  'id_ed25519',
]);

const secretAttachmentExtensions = new Set(['.pem', '.key', '.p12', '.pfx', '.jks', '.keystore', '.ppk']);

const secretEnvTemplateSuffixes = ['.example', '.sample', '.template'];

const withoutControlCharacters = (fileName: string) =>
  [...fileName.normalize('NFC')]
    .filter((char) => {
      const code = char.codePointAt(0) ?? 0;
      return code >= 0x20 && code !== 0x7f;
    })
    .join('');

/**
 * 비밀값이 흔히 담기는 파일명인지. 형식이 아니라 내용 문제라 MIME 허용 목록으로는 못 막는다.
 * 확장자를 바꿔 올리는 우회(`id_rsa.txt`, `.env.txt`)까지 잡으려고 뒤에 붙은 텍스트 확장자 하나만
 * 벗긴 이름으로 판정한다. 벗기기 전 이름까지 보면 `.env.example.txt`가 템플릿 예외에서 빠진다.
 * 제어문자를 지우고 trim·앞뒤 `-`를 먼저 뺀다. 서버 표시명 정제와 같은 이름을 업로드 전에 거절한다.
 * 서버(`api/src/services/attachment.ts`)가 최종 판정자이고, 이 사본은 업로드 전에 안내하려는 것이다.
 */
export const isSecretAttachmentFileName = (fileName: string): boolean => {
  const baseName = (withoutControlCharacters(fileName).split(/[\\/]/).pop() ?? '')
    .trim()
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  const name = baseName.replace(/\.(txt|md|log)$/, '');
  if (secretAttachmentFileNames.has(name)) return true;
  if (name.startsWith('.env.')) {
    return !secretEnvTemplateSuffixes.some((suffix) => name.endsWith(suffix));
  }
  return secretAttachmentExtensions.has(fileExtension(name));
};

export const getAllowedAttachmentMimeTypes = (targetType?: AttachmentTargetType): readonly string[] =>
  targetType === 'document' ? documentAttachmentMimeTypes : allowedAttachmentMimeTypes;

export const maxAttachmentSizeBytes = 10 * 1024 * 1024;
export const maxAttachmentsPerDaemonTrigger = 10;
export const maxAttachmentsPerTarget = maxAttachmentsPerDaemonTrigger;
